import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { validateEmailList, parseEmailListString } from '@hotjar/webapp/lib/email';
import styled from '@emotion/styled';
import { Button, Field, Modal, TextInput } from '@hotjar/hotsauce';
import type { EmailRecipients } from './api/emailRecipients';
import { translationDomain } from './common';

interface Props {
  isOpen: boolean;
  close(): void;
  currentEmailRecipients: string[];
  persistEmailRecipients(emails: EmailRecipients): Promise<void>;
}

function emailListToString(emailList: EmailRecipients) {
  return emailList.join(', ');
}

const S = {
  Field: styled(Field)`
    width: 100%;
  `,
};

const FORM_ID = 'billing-notification-modal-form';

export function BillingNotificationsModal({
  isOpen,
  close,
  currentEmailRecipients,
  persistEmailRecipients,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [emailRecipients, setEmailRecipients] = React.useState<string>('');
  const [emailsFormatError, setEmailsFormatError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const cleanupModal = (emailsList?: EmailRecipients) => {
    setEmailsFormatError(false);
    setEmailRecipients(emailListToString(emailsList ?? currentEmailRecipients));
    setIsLoading(false);
    close();
  };

  const onSubmit = async (event: React.FormEvent) => {
    const emailsList = parseEmailListString(emailRecipients) as EmailRecipients;
    const emailsAreValid = validateEmailList(emailsList);

    event.preventDefault();
    setEmailsFormatError(!emailsAreValid);

    if (emailsAreValid) {
      setIsLoading(true);
      await persistEmailRecipients(emailsList);
      cleanupModal(emailsList);
    }
  };

  React.useEffect(() => {
    setEmailRecipients(emailListToString(currentEmailRecipients));
  }, [currentEmailRecipients]);

  const inputLabel = 'emailRecipients;';
  return (
    <Modal
      contentLabel={t(`${translationDomain}.title`)}
      isOpen={isOpen}
      size="m"
      onClose={() => cleanupModal()}
    >
      <Modal.Header title={t(`${translationDomain}.title`)} />
      <Modal.Body>
        <form onSubmit={onSubmit} id={FORM_ID}>
          <p>{t(`${translationDomain}.description`)}</p>
          <div>
            <S.Field
              inputId={inputLabel}
              labelText={t(`${translationDomain}.emails`)}
              isInvalid={emailsFormatError}
              invalidText={t(`${translationDomain}.format-error`)}
            >
              <TextInput
                id={inputLabel}
                value={emailRecipients}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEmailRecipients(event.target.value)
                }
                placeholder={t(`${translationDomain}.placeholder`)}
                isInvalid={emailsFormatError}
              />
            </S.Field>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => cleanupModal()}>
          {t(`${translationDomain}.cancel`)}
        </Button>
        <Button type="submit" form={FORM_ID} isLoading={isLoading}>
          {t(`${translationDomain}.update`)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
